import React, { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';

type Item = any;

type UpsertMatcher<T> = ((v: T, i: number) => boolean) | string | undefined;

interface Actions<T = Item> {
  setItems: React.Dispatch<React.SetStateAction<T[]>>;
  addItem: (item: T) => void;
  upsertItem: (item: T | ((prev: T) => T), matcher?: UpsertMatcher<T>) => void;
  removeItem: (predicateOrIndex: ((v: T, i: number) => boolean) | number) => void;
  updateItem: (indexOrPredicate: any, updater?: any) => void;
  setAll: (arr: T[] | undefined) => void;
  clear: () => void;
}

const ItemsContext = createContext<Item[] | undefined>(undefined);
const ActionsContext = createContext<Actions | undefined>(undefined);
const ArrayDataContext = createContext<{ items: Item[] } & Actions | undefined>(undefined);

export const ArrayDataProvider = ({ children, initialValue = [] }: { children: ReactNode; initialValue?: any[] }) => {
  const [items, setItems] = useState<Item[]>(Array.isArray(initialValue) ? initialValue : []);

  const upsertItem = useCallback((item: any, matcher?: UpsertMatcher<Item>) => {
    setItems((prev) => {
      if (!item) return prev;
      const isFn = typeof matcher === 'function';
      let idx = -1;
      if (isFn) {
        idx = prev.findIndex((v, i) => (matcher as any)(v, i));
      } else if (typeof matcher === 'string') {
        idx = prev.findIndex((v) => v && item && (v as any)[matcher] === (item as any)[matcher]);
      } else if (item && (item as any).id !== undefined) {
        idx = prev.findIndex((v) => v && (v as any).id === (item as any).id);
      }

      if (idx === -1) {
        return [...prev, item];
      }

      const next = prev.slice();
      next[idx] = typeof item === 'function' ? (item as any)(prev[idx]) : { ...prev[idx], ...item };
      return next;
    });
  }, []);

  const addItem = useCallback((item: Item) => upsertItem(item), [upsertItem]);

  const removeItem = useCallback((predicateOrIndex: ((v: Item, i: number) => boolean) | number) => {
    setItems((prev) => {
      if (typeof predicateOrIndex === 'function') {
        return prev.filter((v, i) => !(predicateOrIndex as any)(v, i));
      }
      return prev.filter((_, i) => i !== (predicateOrIndex as number));
    });
  }, []);

  const updateItem = useCallback((indexOrPredicate: any, updater?: any) => {
    if (indexOrPredicate && typeof indexOrPredicate === 'object' && (updater === undefined || typeof updater === 'string' || typeof updater === 'function')) {
      upsertItem(indexOrPredicate, updater);
      return;
    }

    setItems((prev) => prev.map((item, i) => {
      if (typeof indexOrPredicate === 'function') {
        if (indexOrPredicate(item, i)) {
          return typeof updater === 'function' ? updater(item, i) : updater;
        }
        return item;
      }
      if (i === indexOrPredicate) {
        return typeof updater === 'function' ? updater(item, i) : updater;
      }
      return item;
    }));
  }, [upsertItem]);

  const setAll = useCallback((arr?: Item[]) => {
    setItems(Array.isArray(arr) ? [...arr] : []);
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const actions = useMemo(() => ({
    setItems,
    addItem,
    upsertItem,
    removeItem,
    updateItem,
    setAll,
    clear,
  }), [setItems, addItem, upsertItem, removeItem, updateItem, setAll, clear]);

  const combined = useMemo(() => ({ items, ...actions }), [items, actions]);

  return (
    <ItemsContext.Provider value={items}>
      <ActionsContext.Provider value={actions}>
        <ArrayDataContext.Provider value={combined}>{children}</ArrayDataContext.Provider>
      </ActionsContext.Provider>
    </ItemsContext.Provider>
  );
};

export const useArrayItems = () => {
  const ctx = useContext(ItemsContext);
  if (ctx === undefined) throw new Error('useArrayItems must be used inside ArrayDataProvider');
  return ctx;
};

export const useArrayActions = () => {
  const ctx = useContext(ActionsContext);
  if (ctx === undefined) throw new Error('useArrayActions must be used inside ArrayDataProvider');
  return ctx;
};

export const useArrayData = () => {
  const ctx = useContext(ArrayDataContext);
  if (!ctx) throw new Error('useArrayData must be used inside ArrayDataProvider');
  return ctx;
};

export default ArrayDataContext;
